"use client";

import posthog from "posthog-js";

/**
 * Send a client-side error to PostHog Error Tracking.
 *
 * `posthog-js` is initialized in `instrumentation-client.ts` with
 * `capture_exceptions: true`, which auto-captures `window.onerror` and
 * `unhandledrejection`. That covers errors that propagate to the runtime;
 * use this helper for errors that get *caught* (try/catch around fetches,
 * mutations, file processing, etc.) so they aren't invisible to telemetry.
 *
 * Wraps non-Error values into `Error` so PostHog has a real stack frame to
 * dedupe and group on. Swallows its own failures so a broken telemetry
 * pipeline never escalates a recoverable UI error into a blank screen.
 *
 * The `context` tag is the most useful filter in the PostHog UI — pick
 * something stable like `"admin.users.fetch"` or `"chat.attachment.upload"`
 * so you can group occurrences by call site rather than by stack trace.
 */
export function captureClientError(
  err: unknown,
  context: string,
  properties?: Record<string, unknown>,
): void {
  const errorObject =
    err instanceof Error
      ? err
      : new Error(typeof err === "string" ? err : safeStringify(err));
  try {
    posthog.captureException(errorObject, {
      context,
      ...properties,
    });
  } catch {
    // Telemetry must never break the UI.
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
